import { Grid, Stack, themes, useMediaQuery } from '@ace-clublabs/shared-component-library';
import MembershipLevelCard from './MembershipLevelCard';
import { FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { INewMembershipInfo } from '@lib/types';
import { MembershipBenefit } from './MembershipBenefit';
import { getRecurringPeriodLabel } from '../../utils/stringHelper';
import { getBaseCostByMembershipLevel, getCostByMembershipLevel, MembershipTerm } from '../../utils/helpers';
import { NewJoinSessionData } from '../../application/models/NewJoinSessionData';
import { benefitsHelper } from '../../utils/benefitsHelper';

interface MembershipInfoMobileProps {
  membershipInfo: INewMembershipInfo | null;
  sessionData: NewJoinSessionData;
  selectedMembershipLevel: MembershipLevel;
  selectedTerm: MembershipTerm;
  handleButtonClick: (memLevel: MembershipLevel) => void;
  handleContinueClick: () => void;
  handleSelectAndContinue: () => (memLevel: MembershipLevel) => void;
  isUpgrading?: boolean;
  admissionDiscount: number;
  admissionFee: number;
  discountApplied?: boolean;
  onShowAllBenefits?: () => void;
}

export const MembershipInfo: (props: MembershipInfoMobileProps) => JSX.Element = ({
  membershipInfo,
  selectedMembershipLevel,
  selectedTerm,
  sessionData,
  handleButtonClick,
  handleContinueClick,
  handleSelectAndContinue,
  isUpgrading,
  admissionDiscount,
  admissionFee,
  discountApplied,
  onShowAllBenefits,
}) => {
  const isMonthlySelected = selectedTerm === 'monthly';
  const additionalBenefits = (memLevel: MembershipLevel) => {
    return benefitsHelper.getBenefitsByMembershipLevel(sessionData, memLevel);
  };

  const isDesktop = useMediaQuery(themes.light.breakpoints.up('lg'));
  const allowedMembershipLevels = membershipInfo?.allowedMembershipLevels.map((memLevel) => memLevel);

  const membershipInfoMobile = (
    <>
      <Stack
        direction="row"
        gap="0.25rem"
        justifyContent="space-between"
        id="membershipLevelSelectButtons"
        sx={{ width: '100%', paddingTop: '1.5rem' }}
      >
        {membershipInfo?.allowedMembershipLevels.map((memLevel, index) => {
          const priceAfterDiscount = getCostByMembershipLevel(membershipInfo, memLevel, selectedTerm, sessionData);
          const showPriceAsFree = priceAfterDiscount === 0;
          return (
            <MembershipLevelCard
              key={index}
              selected={selectedMembershipLevel === memLevel}
              planLabel={memLevel}
              priceAfterDiscount={priceAfterDiscount}
              baseCost={getBaseCostByMembershipLevel(memLevel, membershipInfo, selectedTerm)}
              recurringPeriodLabel={getRecurringPeriodLabel(sessionData, isMonthlySelected, discountApplied)}
              showPriceAsFree={showPriceAsFree}
              onClick={() => handleButtonClick(memLevel)}
            />
          );
        })}
      </Stack>
      <MembershipBenefit
        sessionData={sessionData}
        additionalBenefits={additionalBenefits(selectedMembershipLevel)}
        membershipLevel={selectedMembershipLevel}
        onContinue={() => handleContinueClick()}
        onShowBenefitsTable={onShowAllBenefits}
        isUpgrading={isUpgrading}
        admissionFee={admissionFee}
        admissionDiscount={admissionDiscount}
      />
    </>
  );

  const levelsSize = membershipInfo?.allowedMembershipLevels?.length ?? 1;

  const membershipInfoDesktop = (
    <Grid container columnSpacing={'1rem'} columns={12} direction="row" margin="2rem 0">
      {membershipInfo?.allowedMembershipLevels.map((memLevel, index) => {
        const onSelectAndContinue = handleSelectAndContinue();
        const priceAfterDiscount = getCostByMembershipLevel(membershipInfo, memLevel, selectedTerm, sessionData);
        const showPriceAsFree = priceAfterDiscount === 0;
        return (
          <Grid key={memLevel} size={Math.min(12 / levelsSize, 6)} offset={levelsSize > 1 ? 0 : 3} display="flex">
            <MembershipLevelCard
              key={index}
              selected={selectedMembershipLevel === memLevel}
              planLabel={memLevel}
              priceAfterDiscount={priceAfterDiscount}
              baseCost={getBaseCostByMembershipLevel(memLevel, membershipInfo, selectedTerm)}
              recurringPeriodLabel={getRecurringPeriodLabel(sessionData, isMonthlySelected, discountApplied)}
              showPriceAsFree={showPriceAsFree}
              onClick={() => handleButtonClick(memLevel)}
              isDesktop={isDesktop}
            >
              <MembershipBenefit
                allowedMembershipLevels={allowedMembershipLevels}
                sessionData={sessionData}
                additionalBenefits={additionalBenefits(memLevel)}
                membershipLevel={memLevel}
                onContinue={() => onSelectAndContinue(memLevel)}
                isUpgrading={isUpgrading}
                admissionFee={admissionFee}
                admissionDiscount={admissionDiscount}
              />
            </MembershipLevelCard>
          </Grid>
        );
      })}
    </Grid>
  );

  return isDesktop ? membershipInfoDesktop : membershipInfoMobile;
};
